import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, AlertTriangle, Target, Users, Package, Truck, Wallet, Sparkles, Info } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

// ------------------- Global Filter Bar -------------------
function FilterBar({ filters, setFilters, products, customers, suppliers, categories }) {
    return (
        <div className="rounded-lg border border-zinc-200 bg-white p-3 mb-6" data-testid="bi-filters">
            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[140px]">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">From</Label>
                    <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="h-9 text-xs" data-testid="bi-filter-from" />
                </div>
                <div className="min-w-[140px]">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">To</Label>
                    <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="h-9 text-xs" data-testid="bi-filter-to" />
                </div>
                <FilterSelect label="Product" value={filters.product_id} onChange={(v) => setFilters({ ...filters, product_id: v })}
                    options={products.map((p) => ({ value: p.id, label: `${p.sku} · ${p.name}` }))} testId="bi-filter-product" />
                <FilterSelect label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })}
                    options={categories.map((c) => ({ value: c, label: c }))} testId="bi-filter-category" />
                <FilterSelect label="Customer" value={filters.customer_id} onChange={(v) => setFilters({ ...filters, customer_id: v })}
                    options={customers.map((c) => ({ value: c.id, label: c.name }))} testId="bi-filter-customer" />
                <FilterSelect label="Supplier" value={filters.supplier_id} onChange={(v) => setFilters({ ...filters, supplier_id: v })}
                    options={suppliers.map((s) => ({ value: s.id, label: s.name }))} testId="bi-filter-supplier" />
                <div className="ml-auto flex gap-1">
                    {[
                        { label: "7d", days: 6 },
                        { label: "30d", days: 29 },
                        { label: "90d", days: 89 },
                        { label: "YTD", days: null },
                    ].map((p) => (
                        <button key={p.label}
                            onClick={() => {
                                if (p.days === null) {
                                    const y = new Date(); y.setMonth(0); y.setDate(1);
                                    setFilters({ ...filters, from_date: y.toISOString().slice(0, 10), to_date: today() });
                                } else {
                                    setFilters({ ...filters, from_date: daysAgo(p.days), to_date: today() });
                                }
                            }}
                            className="rounded border border-zinc-200 bg-white px-2 py-1 text-[11px] text-zinc-600 hover:border-zinc-400 hover:text-zinc-900 transition-colors"
                            data-testid={`bi-preset-${p.label}`}
                        >{p.label}</button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testId }) {
    return (
        <div className="min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
            <Select value={value || "_all"} onValueChange={(v) => onChange(v === "_all" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs" data-testid={testId}><SelectValue placeholder={`All ${label.toLowerCase()}s`} /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="_all">All {label.toLowerCase()}s</SelectItem>
                    {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );
}

// ------------------- KPI -------------------
function Kpi({ label, value, change, hint, testId, tone }) {
    const toneMap = {
        good: "text-emerald-700", bad: "text-red-700", warn: "text-amber-700", default: "text-zinc-950",
    };
    return (
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testId}>
            <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">{label}</div>
            <div className={`mt-2 text-2xl font-display tracking-tight ${toneMap[tone || "default"]}`}>{value}</div>
            {(change !== undefined && change !== null) && (
                <div className={`mt-1 flex items-center gap-1 text-xs ${change >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {change > 0 ? "+" : ""}{change}% vs prior
                </div>
            )}
            {hint && <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>}
        </div>
    );
}

// ------------------- Sparkline (bar) -------------------
function BarSpark({ series, height = 80 }) {
    if (!series?.length) return <div className="text-xs text-zinc-500">No data</div>;
    const max = Math.max(...series.map((s) => s.value), 1);
    return (
        <div className="flex items-end gap-0.5" style={{ height }}>
            {series.map((s, i) => (
                <div key={i} className="flex-1 bg-zinc-950 rounded-sm hover:bg-violet-600 transition-colors" title={`${s.label}: ${inr(s.value)}`}
                    style={{ height: `${(s.value / max) * 100}%`, minHeight: "1px" }} />
            ))}
        </div>
    );
}

// ------------------- Main -------------------
export default function BusinessIntelligence() {
    const [filters, setFilters] = useState({ from_date: daysAgo(29), to_date: today(), product_id: "", category: "", customer_id: "", supplier_id: "" });
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [tab, setTab] = useState("executive");

    const [executive, setExecutive] = useState(null);
    const [sales, setSales] = useState(null);
    const [productPerf, setProductPerf] = useState(null);
    const [inventory, setInventory] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [supplierData, setSupplierData] = useState(null);
    const [procurement, setProcurement] = useState(null);
    const [financial, setFinancial] = useState(null);

    const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))].sort(), [products]);

    useEffect(() => {
        Promise.all([api.get("/products"), api.get("/customers"), api.get("/suppliers")])
            .then(([p, c, s]) => { setProducts(p.data.items || []); setCustomers(c.data.items || []); setSuppliers(s.data.items || []); });
    }, []);

    const qs = useMemo(() => {
        const parts = [];
        if (filters.from_date) parts.push(`from_date=${filters.from_date}`);
        if (filters.to_date) parts.push(`to_date=${filters.to_date}`);
        if (filters.product_id) parts.push(`product_id=${filters.product_id}`);
        if (filters.category) parts.push(`category=${encodeURIComponent(filters.category)}`);
        if (filters.customer_id) parts.push(`customer_id=${filters.customer_id}`);
        if (filters.supplier_id) parts.push(`supplier_id=${filters.supplier_id}`);
        return parts.join("&");
    }, [filters]);

    useEffect(() => {
        const load = async () => {
            try {
                const [ex, sl, pp, iv, cu, su, pr, fi] = await Promise.all([
                    api.get(`/bi/executive?${qs}`),
                    api.get(`/bi/sales?${qs}`),
                    api.get(`/bi/products?${qs}`),
                    api.get("/bi/inventory"),
                    api.get(`/bi/customers?${qs}`),
                    api.get(`/bi/suppliers?${qs}`),
                    api.get(`/bi/procurement?${qs}`),
                    api.get(`/bi/financial?${qs}`),
                ]);
                setExecutive(ex.data); setSales(sl.data); setProductPerf(pp.data);
                setInventory(iv.data); setCustomerData(cu.data); setSupplierData(su.data);
                setProcurement(pr.data); setFinancial(fi.data);
            } catch (err) { toast.error(formatApiError(err)); }
        };
        load();
    }, [qs]);

    return (
        <div data-testid="page-business-intelligence">
            <PageHeader
                title="Business Intelligence"
                description="How is the business performing? What needs attention? Where are the biggest opportunities and risks?"
            />
            <div className="p-8 max-w-[1400px]">
                <FilterBar filters={filters} setFilters={setFilters} products={products} customers={customers} suppliers={suppliers} categories={categories} />

                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList data-testid="bi-tabs" className="flex-wrap">
                        <TabsTrigger value="executive" data-testid="bi-tab-executive">Executive Summary</TabsTrigger>
                        <TabsTrigger value="sales" data-testid="bi-tab-sales">Sales</TabsTrigger>
                        <TabsTrigger value="products" data-testid="bi-tab-products">Products</TabsTrigger>
                        <TabsTrigger value="inventory" data-testid="bi-tab-inventory">Inventory</TabsTrigger>
                        <TabsTrigger value="customers" data-testid="bi-tab-customers">Customers</TabsTrigger>
                        <TabsTrigger value="suppliers" data-testid="bi-tab-suppliers">Suppliers</TabsTrigger>
                        <TabsTrigger value="procurement" data-testid="bi-tab-procurement">Procurement</TabsTrigger>
                        <TabsTrigger value="financial" data-testid="bi-tab-financial">Financial</TabsTrigger>
                    </TabsList>

                    <TabsContent value="executive" className="mt-4"><ExecutiveTab data={executive} /></TabsContent>
                    <TabsContent value="sales" className="mt-4"><SalesTab data={sales} /></TabsContent>
                    <TabsContent value="products" className="mt-4"><ProductsTab data={productPerf} /></TabsContent>
                    <TabsContent value="inventory" className="mt-4"><InventoryTab data={inventory} /></TabsContent>
                    <TabsContent value="customers" className="mt-4"><CustomersTab data={customerData} /></TabsContent>
                    <TabsContent value="suppliers" className="mt-4"><SuppliersTab data={supplierData} /></TabsContent>
                    <TabsContent value="procurement" className="mt-4"><ProcurementTab data={procurement} /></TabsContent>
                    <TabsContent value="financial" className="mt-4"><FinancialTab data={financial} /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

// ------------------- Section tabs -------------------
function ExecutiveTab({ data }) {
    if (!data) return <SkeletonBlock />;
    const change = data.performance.change_pct;
    return (
        <div className="space-y-4" data-testid="bi-executive">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Kpi testId="bi-kpi-revenue" label="Revenue" value={inr(data.performance.revenue_current)} change={change} hint={`vs prior ${inr(data.performance.revenue_previous)}`} tone={change >= 0 ? "good" : "bad"} />
                <Kpi testId="bi-kpi-overdue" label="Overdue Value" value={inr(data.attention.overdue_value)} hint={`${data.attention.overdue_invoices} invoice${data.attention.overdue_invoices === 1 ? "" : "s"}`} tone={data.attention.overdue_value > 0 ? "warn" : "good"} />
                <Kpi testId="bi-kpi-low-stock" label="Low Stock SKUs" value={data.attention.low_stock_skus} hint="need reorder soon" tone={data.attention.low_stock_skus > 0 ? "warn" : "good"} />
                <Kpi testId="bi-kpi-out-of-stock" label="Out of Stock" value={data.attention.out_of_stock_skus} hint="lost sale risk" tone={data.attention.out_of_stock_skus > 0 ? "bad" : "good"} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ListCard title="Biggest opportunities" subtitle="Top revenue products this period" icon={Target} testId="bi-exec-opportunities">
                    {data.opportunities.top_products.length === 0 ? <Empty label="No sales in period" />
                        : data.opportunities.top_products.map((p, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                                <div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{p.sku}</div><div className="text-sm">{p.name}</div></div>
                                <div className="font-mono text-sm">{inr(p.revenue)}</div>
                            </div>
                        ))}
                </ListCard>
                <ListCard title="Biggest risks" subtitle="Customers with highest overdue balance" icon={AlertTriangle} testId="bi-exec-risks">
                    {data.risks.top_overdue_customers.length === 0 ? <Empty label="No overdue customers" />
                        : data.risks.top_overdue_customers.map((c, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                                <div className="text-sm">{c.name}</div>
                                <div className="font-mono text-sm text-red-700">{inr(c.outstanding)}</div>
                            </div>
                        ))}
                </ListCard>
            </div>
            <div className="rounded-md border border-violet-100 bg-violet-50/40 px-4 py-3 text-xs text-violet-900 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
                Ask Yug Brain to explain any number here — the same data powers its answers.
            </div>
        </div>
    );
}

function SalesTab({ data }) {
    if (!data) return <SkeletonBlock />;
    return (
        <div className="space-y-4" data-testid="bi-sales">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Kpi testId="bi-sales-revenue" label="Revenue" value={inr(data.kpis.revenue)} />
                <Kpi testId="bi-sales-invoices" label="Invoices" value={data.kpis.invoices} />
                <Kpi testId="bi-sales-customers" label="Unique Customers" value={data.kpis.unique_customers} />
                <Kpi testId="bi-sales-aov" label="Avg Order Value" value={inr(data.kpis.average_order_value)} />
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white p-5">
                <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500 mb-3">Daily revenue</div>
                <BarSpark series={data.daily_revenue.map((d) => ({ label: d.date, value: d.revenue }))} height={120} />
            </div>
        </div>
    );
}

function ProductsTab({ data }) {
    if (!data) return <SkeletonBlock />;
    return (
        <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="bi-products">
            <div className="px-4 py-3 border-b border-zinc-100 text-sm font-medium">Top products by revenue</div>
            {data.items.length === 0 ? <Empty label="No sales in period" /> : (
                <Table>
                    <TableHeader><TableRow className="bg-zinc-50/40">
                        <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">SKU</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">Product</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Qty</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Revenue</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Orders</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                        {data.items.map((r) => (
                            <TableRow key={r.product_id} data-testid={`bi-product-${r.product_id}`}>
                                <TableCell className="font-mono text-xs">{r.sku}</TableCell>
                                <TableCell className="text-sm">{r.name}</TableCell>
                                <TableCell className="text-right font-mono text-xs">{r.quantity}</TableCell>
                                <TableCell className="text-right font-mono text-xs">{inr(r.revenue)}</TableCell>
                                <TableCell className="text-right font-mono text-xs">{r.orders}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
}

function InventoryTab({ data }) {
    if (!data) return <SkeletonBlock />;
    return (
        <div className="space-y-4" data-testid="bi-inventory">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi testId="bi-inv-units" label="Total Units" value={data.kpis.total_units.toLocaleString("en-IN")} />
                <Kpi testId="bi-inv-value" label="Inventory Value" value={inr(data.kpis.total_value)} />
                <Kpi testId="bi-inv-healthy" label="Healthy" value={data.kpis.healthy} tone="good" />
                <Kpi testId="bi-inv-low" label="Low Stock" value={data.kpis.low_stock} tone={data.kpis.low_stock > 0 ? "warn" : "good"} />
                <Kpi testId="bi-inv-out" label="Out of Stock" value={data.kpis.out_of_stock} tone={data.kpis.out_of_stock > 0 ? "bad" : "good"} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ListCard title="Low stock alerts" icon={AlertTriangle} testId="bi-inv-low-list">
                    {data.low_stock.length === 0 ? <Empty label="Nothing running low" /> :
                        data.low_stock.map((r) => (
                            <div key={r.product_id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                                <div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{r.sku}</div><div className="text-sm">{r.name}</div></div>
                                <div className="font-mono text-sm text-amber-700">{r.quantity} left</div>
                            </div>
                        ))}
                </ListCard>
                <ListCard title="Out of stock" icon={Package} testId="bi-inv-out-list">
                    {data.out_of_stock.length === 0 ? <Empty label="Nothing out of stock" /> :
                        data.out_of_stock.map((r) => (
                            <div key={r.product_id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                                <div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{r.sku}</div><div className="text-sm">{r.name}</div></div>
                                <div className="text-xs text-red-700 font-medium">0</div>
                            </div>
                        ))}
                </ListCard>
            </div>
        </div>
    );
}

function CustomersTab({ data }) {
    if (!data) return <SkeletonBlock />;
    return (
        <div className="space-y-4" data-testid="bi-customers">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Kpi testId="bi-cust-active" label="Active Customers" value={data.kpis.active_customers} />
                <Kpi testId="bi-cust-revenue" label="Total Revenue" value={inr(data.kpis.total_revenue)} />
                <Kpi testId="bi-cust-outstanding" label="Outstanding" value={inr(data.kpis.total_outstanding)} tone={data.kpis.total_outstanding > 0 ? "warn" : "good"} />
                <Kpi testId="bi-cust-atrisk" label="At-Risk (60d+)" value={data.kpis.at_risk_count} tone={data.kpis.at_risk_count > 0 ? "warn" : "good"} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ListCard title="Top customers" icon={Users} testId="bi-cust-top">
                    {data.top_customers.length === 0 ? <Empty label="No customers in period" /> :
                        data.top_customers.map((c) => (
                            <div key={c.customer_id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                                <div><div className="text-sm">{c.name}</div><div className="text-[10px] text-zinc-500">{c.orders} orders</div></div>
                                <div className="text-right"><div className="font-mono text-sm">{inr(c.revenue)}</div><div className="text-[10px] text-zinc-500">{inr(c.outstanding)} due</div></div>
                            </div>
                        ))}
                </ListCard>
                <ListCard title="At-risk customers" subtitle="No orders in 60+ days" icon={AlertTriangle} testId="bi-cust-risk">
                    {data.at_risk.length === 0 ? <Empty label="All customers active" /> :
                        data.at_risk.map((c) => (
                            <div key={c.customer_id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                                <div className="text-sm">{c.name}</div>
                                <div className="font-mono text-xs text-amber-700">{c.days_since_last_order}d ago</div>
                            </div>
                        ))}
                </ListCard>
            </div>
        </div>
    );
}

function SuppliersTab({ data }) {
    if (!data) return <SkeletonBlock />;
    return (
        <div className="space-y-4" data-testid="bi-suppliers">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Kpi testId="bi-sup-active" label="Active Suppliers" value={data.kpis.active_suppliers} />
                <Kpi testId="bi-sup-spend" label="Total Spend" value={inr(data.kpis.total_spend)} />
                <Kpi testId="bi-sup-payable" label="Outstanding Payable" value={inr(data.kpis.total_payable)} tone={data.kpis.total_payable > 0 ? "warn" : "good"} />
            </div>
            <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-100 text-sm font-medium">Top suppliers by spend</div>
                {data.top_suppliers.length === 0 ? <Empty label="No POs in period" /> : (
                    <Table>
                        <TableHeader><TableRow className="bg-zinc-50/40">
                            <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">Supplier</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Orders</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Spend</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Avg Lead Days</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Received %</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Payable</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                            {data.top_suppliers.map((s) => (
                                <TableRow key={s.supplier_id} data-testid={`bi-supplier-${s.supplier_id}`}>
                                    <TableCell className="text-sm">{s.name}</TableCell>
                                    <TableCell className="text-right font-mono text-xs">{s.orders}</TableCell>
                                    <TableCell className="text-right font-mono text-xs">{inr(s.spend)}</TableCell>
                                    <TableCell className="text-right font-mono text-xs">{s.lead_time_avg_days || "—"}</TableCell>
                                    <TableCell className="text-right font-mono text-xs">{s.on_time_rate}%</TableCell>
                                    <TableCell className="text-right font-mono text-xs">{inr(s.outstanding_payable)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>
    );
}

function ProcurementTab({ data }) {
    if (!data) return <SkeletonBlock />;
    return (
        <div className="space-y-4" data-testid="bi-procurement">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Kpi testId="bi-proc-total" label="Total POs" value={data.kpis.total_pos} />
                <Kpi testId="bi-proc-value" label="Total Value" value={inr(data.kpis.total_value)} />
                <Kpi testId="bi-proc-avg" label="Avg PO Value" value={inr(data.kpis.avg_po_value)} />
            </div>
            <ListCard title="POs by status" icon={Truck} testId="bi-proc-status">
                {data.by_status.length === 0 ? <Empty label="No POs in period" /> :
                    data.by_status.map((s) => (
                        <div key={s.status} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                            <div className="text-sm capitalize">{s.status}</div>
                            <div className="font-mono text-sm">{s.count}</div>
                        </div>
                    ))}
            </ListCard>
        </div>
    );
}

function FinancialTab({ data }) {
    if (!data) return <SkeletonBlock />;
    const k = data.kpis;
    return (
        <div className="space-y-4" data-testid="bi-financial">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi testId="bi-fin-revenue" label="Revenue (Accrual)" value={inr(k.revenue_accrual)} />
                <Kpi testId="bi-fin-cogs" label="COGS" value={inr(k.cogs)} />
                <Kpi testId="bi-fin-gp" label="Gross Profit" value={inr(k.gross_profit)} hint={`${k.gross_margin_pct}% margin`} tone={k.gross_profit >= 0 ? "good" : "bad"} />
                <Kpi testId="bi-fin-net" label="Net Cash" value={inr(k.net_cash)} tone={k.net_cash >= 0 ? "good" : "bad"} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Kpi testId="bi-fin-cashin" label="Cash In" value={inr(k.cash_in)} icon={Wallet} />
                <Kpi testId="bi-fin-opex" label="Operating Expenses" value={inr(k.operating_expenses)} />
                <Kpi testId="bi-fin-payouts" label="Supplier Payouts" value={inr(k.supplier_payouts)} />
            </div>
            <div className="rounded-md border border-zinc-200 bg-zinc-50/40 px-4 py-3 text-xs text-zinc-600 flex items-center gap-2">
                <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
                Accrual metrics use invoice send dates. Cash-basis metrics use payment dates. Gross Profit uses each product&apos;s cost price.
            </div>
        </div>
    );
}

function ListCard({ title, subtitle, icon: Icon, children, testId }) {
    return (
        <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid={testId}>
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                        {Icon && <Icon className="h-3.5 w-3.5 text-zinc-500" strokeWidth={1.5} />}
                        {title}
                    </div>
                    {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
                </div>
            </div>
            <div className="px-4 py-2">{children}</div>
        </div>
    );
}

function Empty({ label }) {
    return <div className="py-6 text-center text-xs text-zinc-500">{label}</div>;
}

function SkeletonBlock() {
    return (
        <div className="space-y-3">
            <div className="h-24 bg-zinc-100 animate-pulse rounded-lg" />
            <div className="h-40 bg-zinc-100 animate-pulse rounded-lg" />
        </div>
    );
}
